import React, { useEffect, useRef, useState } from 'react'
import { Check, Copy, Search, X } from 'lucide-react'
import { copyToClipboard, splitHighlight, value } from '../../utils/parser'

export type Tone = 'gray' | 'green' | 'amber' | 'red' | 'blue' | 'violet'

const TONES: Record<Tone, string> = {
  gray: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
  green: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200',
  amber: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  red: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
  blue: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
  violet: 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200',
}

export function Badge({ children, tone = 'gray', title, className = '' }: { children: React.ReactNode; tone?: Tone; title?: string; className?: string }) {
  return (
    <span title={title} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap ${TONES[tone]} ${className}`}>
      {children}
    </span>
  )
}

export function Metric({ label, value: metric, hint, tone }: { label: string; value: unknown; hint?: string; tone?: 'amber' | 'red' | 'green' }) {
  const toneClass = tone === 'amber' ? 'text-amber-700 dark:text-amber-300' : tone === 'red' ? 'text-red-700 dark:text-red-300' : tone === 'green' ? 'text-green-700 dark:text-green-300' : 'text-gray-900 dark:text-white'
  return (
    <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg" title={hint}>
      <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
      <div className={`font-semibold ${toneClass}`}>{value(metric)}</div>
    </div>
  )
}

export function Section({ title, description, actions, children }: { title: string; description?: string; actions?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
          {description && <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-3xl">{description}</p>}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {children}
    </section>
  )
}

export function Notice({ children, tone = 'amber' }: { children: React.ReactNode; tone?: 'amber' | 'blue' | 'red' }) {
  const styles = {
    amber: 'border-amber-200 dark:border-amber-900 bg-amber-50/60 dark:bg-amber-950/20 text-amber-800 dark:text-amber-200',
    blue: 'border-blue-200 dark:border-blue-900 bg-blue-50/60 dark:bg-blue-950/20 text-blue-800 dark:text-blue-200',
    red: 'border-red-200 dark:border-red-900 bg-red-50/60 dark:bg-red-950/20 text-red-800 dark:text-red-200',
  }
  return <div className={`border rounded-lg p-4 text-sm ${styles[tone]}`}>{children}</div>
}

export function SegmentedControl<T extends string>({ options, valueKey, onChange, label }: {
  options: ReadonlyArray<readonly [T, string]>
  valueKey: T
  onChange: (next: T) => void
  label: string
}) {
  return (
    <div className="inline-flex rounded-md border border-gray-200 dark:border-gray-700 overflow-hidden" role="group" aria-label={label}>
      {options.map(([key, text]) => (
        <button
          key={key}
          type="button"
          aria-pressed={valueKey === key}
          onClick={() => onChange(key)}
          className={`px-3 py-1.5 text-xs font-medium transition-colors ${valueKey === key ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'}`}
        >
          {text}
        </button>
      ))}
    </div>
  )
}

/** Search box; "/" focuses it from anywhere on the page (unless typing). */
export function SearchInput({ value: query, onChange, placeholder, className = '' }: { value: string; onChange: (next: string) => void; placeholder: string; className?: string }) {
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null
      const typing = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)
      if (event.key === '/' && !typing && !event.metaKey && !event.ctrlKey && !event.altKey) {
        event.preventDefault()
        ref.current?.focus()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])
  return (
    <div className={`relative ${className}`}>
      <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden={true} />
      <input
        ref={ref}
        type="search"
        value={query}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => { if (event.key === 'Escape') { onChange(''); event.currentTarget.blur() } }}
        placeholder={placeholder}
        aria-label={placeholder}
        className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-1.5 pl-8 pr-14 text-sm text-gray-900 dark:text-white placeholder:text-gray-400"
      />
      {query ? (
        <button type="button" onClick={() => onChange('')} className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200" aria-label="Clear search">
          <X className="h-4 w-4" aria-hidden={true} />
        </button>
      ) : (
        <kbd className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 rounded border border-gray-200 dark:border-gray-600 px-1.5 text-2xs text-gray-400">/</kbd>
      )}
    </div>
  )
}

export function CopyButton({ text, label = 'Copy', copiedLabel = 'Copied', className = 'btn-secondary', iconOnly = false }: { text: string | (() => string); label?: string; copiedLabel?: string; className?: string; iconOnly?: boolean }) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle')
  useEffect(() => {
    if (state === 'idle') return
    const id = window.setTimeout(() => setState('idle'), 1800)
    return () => window.clearTimeout(id)
  }, [state])
  return (
    <button
      type="button"
      className={className}
      title={iconOnly ? label : undefined}
      aria-label={iconOnly ? label : undefined}
      onClick={async () => setState((await copyToClipboard(typeof text === 'function' ? text() : text)) ? 'copied' : 'failed')}
    >
      {state === 'copied' ? <Check className="h-4 w-4" aria-hidden={true} /> : <Copy className="h-4 w-4" aria-hidden={true} />}
      {!iconOnly && <span className="ml-1.5">{state === 'copied' ? copiedLabel : state === 'failed' ? 'Copy failed' : label}</span>}
    </button>
  )
}

export function Highlight({ text, query }: { text: string; query: string }) {
  return (
    <>
      {splitHighlight(text, query).map((part, index) => part.match
        ? <mark key={index} className="rounded bg-yellow-200/80 px-0.5 text-inherit dark:bg-yellow-700/50">{part.text}</mark>
        : <React.Fragment key={index}>{part.text}</React.Fragment>)}
    </>
  )
}
